import { estimateGr, type Id } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import type { ArticleRow, ListItemRow } from '../db/types';
import { buildMergeMap, resolveWith } from './products';

// Ceny z paragonów przy pozycjach list: artykuł przypięty do pozycji albo najnowszy artykuł jej produktu.

export interface PriceIndex {
  /** Artykuły z ceną po id. */
  byId: Map<Id, ArticleRow>;
  /** Produkt (po scaleniach) → jego artykuły, najnowszy zakup pierwszy. */
  byProduct: Map<Id, ArticleRow[]>;
  /** Zwykle kupowana ilość (kg) towarów na wagę — do szacunku, gdy lista nie mówi, ile. */
  typicalKg: Map<Id, number>;
  resolveProduct: (id: Id) => Id;
}

const hasPrice = (a: ArticleRow) => !a.deleted && a.lastShelfGr !== null && a.lastShelfGr !== undefined;

export async function loadPriceIndex(): Promise<PriceIndex> {
  const [articles, products, weighed] = await Promise.all([
    db.articles.toArray(),
    db.products.toArray(),
    db.receiptLines.filter((l) => !!l.weight && !l.deleted).toArray(),
  ]);
  const resolveProduct = resolveWith(buildMergeMap(products));
  const byId = new Map<Id, ArticleRow>();
  const byProduct = new Map<Id, ArticleRow[]>();
  for (const a of articles) {
    if (!hasPrice(a)) continue;
    byId.set(a.id, a);
    if (!a.productId) continue;
    const pid = resolveProduct(a.productId);
    byProduct.set(pid, [...(byProduct.get(pid) ?? []), a]);
  }
  // Najpierw artykuły podpięte pewnie (dopasowanie paragonu do listy, decyzja użytkownika, przypięcie), potem przez AI;
  // w grupie najnowszy zakup pierwszy.
  const trust = (a: ArticleRow) => (a.linkSource === 'ai' ? 1 : 0);
  for (const list of byProduct.values()) list.sort((x, y) => trust(x) - trust(y) || (y.lastBoughtMs ?? 0) - (x.lastBoughtMs ?? 0));
  const kg = new Map<Id, number[]>();
  for (const l of weighed) if (l.articleId && l.qty) kg.set(l.articleId, [...(kg.get(l.articleId) ?? []), l.qty]);
  const typicalKg = new Map([...kg].map(([id, qs]) => [id, median(qs)]));
  return { byId, byProduct, typicalKg, resolveProduct };
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

export function usePriceIndex(): PriceIndex | undefined {
  return useLiveQuery(loadPriceIndex);
}

/** Artykuł z ceną dla pozycji listy: przypięty albo ostatnio kupiony artykuł produktu. */
export function articleFor(item: Pick<ListItemRow, 'articleId' | 'productId'>, index: PriceIndex): ArticleRow | null {
  if (item.articleId) {
    const pinned = index.byId.get(item.articleId);
    if (pinned) return pinned;
  }
  if (!item.productId) return null;
  return index.byProduct.get(index.resolveProduct(item.productId))?.[0] ?? null;
}

/** Szacowany koszt pozycji według ceny półkowej; null = brak ceny. */
export function itemEstimateGr(item: ListItemRow, index: PriceIndex): number | null {
  const a = articleFor(item, index);
  if (!a || a.lastShelfGr === null) return null;
  return estimateGr(item, { unitGr: a.lastShelfGr, weight: !!a.weight, typicalQty: index.typicalKg.get(a.id) ?? null });
}

/** Szacunek listy: suma pozycji z ceną i ile pozycji ją ma. */
export function listEstimate(items: readonly ListItemRow[], index: PriceIndex): { totalGr: number; priced: number; count: number } {
  let totalGr = 0;
  let priced = 0;
  for (const i of items) {
    const gr = itemEstimateGr(i, index);
    if (gr === null) continue;
    totalGr += gr;
    priced++;
  }
  return { totalGr, priced, count: items.length };
}

/** „2 tyg. temu”, „wczoraj”, „3 mies. temu”. */
export function agoLabel(ms: number, now = Date.now()): string {
  const days = Math.floor((now - ms) / 86_400_000);
  if (days <= 0) return 'dziś';
  if (days === 1) return 'wczoraj';
  if (days < 14) return `${days} dni temu`;
  if (days < 60) return `${Math.round(days / 7)} tyg. temu`;
  if (days < 365) return `${Math.round(days / 30)} mies. temu`;
  const years = Math.round(days / 365);
  return years === 1 ? 'rok temu' : `${years} ${years < 5 ? 'lata' : 'lat'} temu`;
}

/** Czytelna nazwa artykułu (rozwinięta przez AI, inaczej kasowa). */
export const articleName = (a: Pick<ArticleRow, 'name' | 'rawName'>) => a.name?.trim() || a.rawName?.trim() || 'artykuł';
